import React from "react";
import { observer } from "mobx-react-lite";
import { Button, ColorPicker, Flex, Tooltip } from "antd";
import { CustomParseFieldProps } from "@opendash/plugin-parse";
import { Icon } from "@opendash/icons";
import Input from "antd/lib/input/Input";
import { FestivalConfig } from "../../../parsegenerated";
import { ScheduleLocation } from "../../FestivalCalcPage/types";

type Props = CustomParseFieldProps<FestivalConfig>;

const DEFAULT_COLOR = "#1677ff";

const ColorDot = ({ color }: { color?: string }) => (
  <span
    style={{
      width: 14,
      height: 14,
      borderRadius: "50%",
      background: color ?? "#d9d9d9",
      flexShrink: 0,
      border: "1px solid rgba(0,0,0,0.12)",
    }}
  />
);

export const LocationCustomField = observer((props: Props) => {
  const [newName, setNewName] = React.useState("");
  const [newColor, setNewColor] = React.useState(DEFAULT_COLOR);

  const [editingId, setEditingId] = React.useState<string | null>(null);
  const [editName, setEditName] = React.useState("");
  const [editColor, setEditColor] = React.useState(DEFAULT_COLOR);

  const startEdit = (loc: ScheduleLocation) => {
    setEditingId(String(loc.id));
    setEditName(loc.name);
    setEditColor(loc.color ?? DEFAULT_COLOR);
  };

  const saveEdit = () => {
    props.setValue(
      (props.value ?? []).map((loc: ScheduleLocation) =>
        String(loc.id) === editingId ? { ...loc, name: editName, color: editColor } : loc
      )
    );
    setEditingId(null);
  };

  const cancelEdit = () => setEditingId(null);

  const handleAdd = () => {
    const entry: ScheduleLocation = {
      id: crypto.randomUUID(),
      name: newName,
      color: newColor,
    };
    props.setValue(props.value ? [...props.value, entry] : [entry]);
    setNewName("");
    setNewColor(DEFAULT_COLOR);
  };

  const handleDelete = (id: string) => {
    props.setValue((props.value ?? []).filter((loc: ScheduleLocation) => String(loc.id) !== id));
  };

  return (
    <>
      {(props.value ?? []).map((loc: ScheduleLocation) => {
        const locId = String(loc.id);
        if (editingId === locId) {
          return (
            <Flex key={locId} align="center" gap={8} style={{ marginBottom: 6 }}>
              <ColorPicker
                value={editColor}
                onChange={(_, hex) => setEditColor(hex)}
              />
              <Input
                value={editName}
                onChange={(e) => setEditName(e.target.value)}
                style={{ flex: 1 }}
                autoFocus
                onPressEnter={saveEdit}
              />
              <Tooltip title="Speichern">
                <Button size="small" type="primary" onClick={saveEdit} disabled={!editName.trim()}>
                  <Icon icon="fa:check" />
                </Button>
              </Tooltip>
              <Tooltip title="Abbrechen">
                <Button size="small" onClick={cancelEdit}>
                  <Icon icon="fa:xmark" />
                </Button>
              </Tooltip>
            </Flex>
          );
        }
        return (
          <Flex key={locId} align="center" gap={8} style={{ marginBottom: 6 }}>
            <ColorDot color={loc.color} />
            <span style={{ flex: 1 }}>{loc.name}</span>
            {props.setValue && (
              <>
                <Tooltip title="Bearbeiten">
                  <Button size="small" onClick={() => startEdit(loc)}>
                    <Icon icon="fa:pen" />
                  </Button>
                </Tooltip>
                <Tooltip title="Löschen">
                  <Button size="small" danger onClick={() => handleDelete(locId)}>
                    <Icon icon="fa:trash" />
                  </Button>
                </Tooltip>
              </>
            )}
          </Flex>
        );
      })}
      {props.setValue && (
        <Flex align="center" gap={8} style={{ marginTop: 12 }}>
          <ColorPicker
            value={newColor}
            onChange={(_, hex) => setNewColor(hex)}
          />
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Neue Location"
            style={{ flex: 1 }}
            onPressEnter={() => newName.trim() && handleAdd()}
          />
          <Button onClick={handleAdd} disabled={!newName.trim()}>
            <Icon icon="fa:plus" />
          </Button>
        </Flex>
      )}
    </>
  );
});
